import React, { Component } from 'react'
import { css } from '@emotion/css'
import { TableEmptyState } from '@contrast-security-inc/design-system-tables'
import { IconWarning } from '@contrast-security-inc/design-system-foundations'
import { FormattedMessage } from 'react-intl'

interface State {
  readonly error?: Error
}

export class AngularErrorBoundary extends Component<
  {
    readonly children: any
  },
  State
> {
  public state: State = { error: undefined }

  public componentDidCatch(error: Error) {
    this.setState({ error })

    // We don't currently have an error reporting service
    // where we could send UI runtime errors. If we did,
    // this is where we'd send them.
  }

  public render() {
    const { error } = this.state

    if (error) {
      this.props?.children(error)

      return isGatewayTimeout(error.message) ? (
        <table
          className={css`
            width: 100%;
            height: 100%;
            justify-content: center;
            align-items: center;
          `}
        >
          <TableEmptyState
            bodyText={<FormattedMessage id="detail_errorMsg_contactSupport" />}
            headerText={<FormattedMessage id="detail_errorMsg_timeout" />}
            iconProps={{
              Icon: IconWarning,
              color: 'neutral50'
            }}
          />
        </table>
      ) : (
        // I don't know what other information would be useful here.
        // After we have some more experience with how this works
        // in practice, we can do something more elaborate.
        <div>
          {`
            There was an error in the React component tree that
            was wrapped by this Angular directive: ${error.message}
         `}
        </div>
      )
    }

    return this.props.children()
  }
}

export function isGatewayTimeout(error?: string) {
  return error !== undefined && error.includes('status=504')
}

export function wrapWithErrorBoundary(WrappedComponent) {
  return function NgDirectiveErrorBoundary(props) {
    return (
      <AngularErrorBoundary
        children={(error: Error) => (
          <WrappedComponent {...props} error={error} />
        )}
      />
    )
  }
}
